import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CircleAlert, CheckCircle2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { adminApi, errMsg, inr, STATUS_LABEL } from "@/lib/api";
import { Panel, PageTitle, Btn, Pill } from "@/admin/ui";

const Stat = ({ label, value, to, tone, testId }) => (
  <Link to={to || "#"} className="bg-white rounded-md border border-stone-200 p-4 hover:border-primary/40 transition-colors" data-testid={testId}>
    <p className="text-[11px] uppercase tracking-wider font-bold text-stone-500">{label}</p>
    <p className={`text-2xl font-extrabold mt-1 ${tone === "warn" && value > 0 ? "text-accent" : "text-primary"}`}>{value}</p>
  </Link>
);

export default function AdminDashboard() {
  const [d, setD] = useState(null);
  const load = () => adminApi.get("/dashboard").then((r) => setD(r.data));
  useEffect(() => { load(); }, []);

  const store = async (body) => {
    try { setD((await adminApi.post("/store", body)).data); toast.success("Store updated"); } catch (e) { toast.error(errMsg(e)); }
  };

  if (!d) return <p className="text-stone-500">Loading…</p>;
  const pricesToday = d.prices_confirmed_date === d.today;

  return (
    <div className="space-y-4">
      <PageTitle sub={`Today · ${d.today}`}>Good morning</PageTitle>

      <Panel title="Store control" testId="store-control-panel">
        <div className="grid md:grid-cols-[1fr_auto] gap-4 items-center">
          <div className="flex gap-3">
            {d.ordering_open ? <CheckCircle2 className="w-6 h-6 text-secondary shrink-0" /> : <CircleAlert className="w-6 h-6 text-accent shrink-0" />}
            <div>
              <p className="font-bold text-[#1A2E1C]" data-testid="ordering-status">{d.ordering_open ? "Ordering is OPEN for today" : "Ordering is CLOSED"}</p>
              <p className="text-sm text-stone-500">{d.ordering_open ? `Prices confirmed for ${d.today}.` : d.closed_reason}</p>
              {d.stale_price_count > 0 && <p className="text-sm text-accent mt-1" data-testid="stale-price-warning">{d.stale_price_count} products have prices not updated today.</p>}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Link to="/admin/products"><Btn variant="outline" data-testid="goto-prices-btn">Update prices & stock</Btn></Link>
            <Btn onClick={() => store({ confirm_prices: true })} data-testid="confirm-prices-btn">{pricesToday ? "Re-confirm today's prices" : "Confirm prices & open ordering"}</Btn>
            <label className="flex items-center gap-2 text-sm font-semibold">
              <Switch checked={d.paused} onCheckedChange={(v) => store({ paused: v })} data-testid="pause-ordering-switch" /> Pause
            </label>
          </div>
        </div>
      </Panel>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <Stat label="Orders today" value={d.orders_today} to={`/admin/orders?date=${d.today}`} testId="stat-orders-today" />
        <Stat label="Tomorrow (cut/combos)" value={d.orders_tomorrow} to="/admin/prep" testId="stat-orders-tomorrow" />
        <Stat label="To confirm" value={d.by_status.placed} to="/admin/orders?status=placed" tone="warn" testId="stat-to-confirm" />
        <Stat label="Payments to verify" value={d.pending_payment_reviews} to="/admin/orders?payment_status=proof_submitted" tone="warn" testId="stat-payments-verify" />
        <Stat label="Awaiting proof" value={d.awaiting_proof} to="/admin/orders?payment_status=awaiting_proof" testId="stat-awaiting-proof" />
        <Stat label="COD approvals" value={d.pending_cod_approvals} to="/admin/orders?cod_approval=pending" tone="warn" testId="stat-cod-approvals" />
        <Stat label="Refunds pending" value={d.pending_refunds} to="/admin/orders" tone="warn" testId="stat-refunds-pending" />
        <Stat label="Net revenue today" value={inr(d.revenue_today)} testId="stat-revenue" />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <Panel title="Today's slots" action={<Link to="/admin/slots" className="text-xs font-semibold text-primary">Manage</Link>} testId="slots-panel">
          <div className="space-y-3">
            {d.slots.map((s) => (
              <div key={s.id} data-testid={`dash-slot-${s.id}`}>
                <div className="flex justify-between text-sm">
                  <span className="font-semibold">{s.label} · {s.start}–{s.end}</span>
                  <span className="text-stone-500">{s.used}/{s.capacity} {!s.available && <Pill tone="orange">{s.reason}</Pill>}</span>
                </div>
                <div className="h-2 rounded bg-stone-100 mt-1 overflow-hidden">
                  <div className="h-full bg-secondary" style={{ width: `${Math.min(100, (s.used / Math.max(1, s.capacity)) * 100)}%` }} />
                </div>
              </div>
            ))}
          </div>
        </Panel>
        <Panel title="Stock alerts" action={<Link to="/admin/products" className="text-xs font-semibold text-primary">Update stock</Link>} testId="stock-alerts-panel">
          {d.low_stock.length === 0 ? <p className="text-sm text-stone-500">All products well stocked.</p> : (
            <div className="divide-y divide-stone-100">
              {d.low_stock.map((p) => (
                <div key={p.id} className="flex justify-between items-center gap-2 py-2 text-sm" data-testid={`low-stock-${p.id}`}>
                  <span className="mr-auto">{p.name} <span className="text-stone-400">({p.unit})</span></span>
                  <span className="text-xs text-stone-500" data-testid={`buy-qty-${p.id}`}>{p.buy_qty ? <>Buy tomorrow: <b className="text-primary">{p.buy_qty}</b></> : "No recent sales"}</span>
                  <Pill tone={p.stock_status === "unavailable" ? "grey" : "orange"}>{p.stock_status === "unavailable" ? "Out of stock" : `${p.available_qty} left`}</Pill>
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>

      <Panel title="Orders by status (today)" testId="status-breakdown">
        <div className="flex flex-wrap gap-2">
          {Object.entries(d.by_status).map(([s, n]) => (
            <Link key={s} to={`/admin/orders?status=${s}`} className="rounded-md border border-stone-200 px-3 py-2 text-sm hover:border-primary/40">{STATUS_LABEL[s]}: <b>{n}</b></Link>
          ))}
        </div>
      </Panel>
    </div>
  );
}
